/**
 * EquipmentDashboard — subtab of the phased Burn Tool Equipment view.
 *
 * A read-only planning/forecast overview that pulls together every equipment
 * cost stream the job carries:
 *   - In-house equipment usage  (cost types 52100–52199)  → hours, unit count, mob/demob
 *   - Outside rental            (cost type  52200)        → "Rentals" tab spend
 *   - Fuel                      (cost type  52300)        → "Other" tab spend
 *   - Repairs / maintenance     (cost type  52400)        → "Other" tab spend
 *
 * Actuals come from jobCostDetail, open POs from jobOpenCommitments, and the
 * in-house plan (planned hours / mob+demob dates) from the phased estimate
 * ledger this view saves under Forecast_Tab = 'Burn Tool Equipment Phased'.
 * Non-phased jobs render this dashboard too — their plan lives under
 * Forecast_Tab = 'Burn Tool Equipment' (row ID = week); when a job carries
 * rows in both ledgers (mode was switched), the phased one wins.
 *
 * The Equipment Plan table also carries per-item rental-tracking fields that
 * mirror the Excel rental log (delivery / pick up / fees / tax dollars,
 * on-rent + off-rent dates, budget). On PO-backed rows the four charge
 * columns are read-only and pulled from the PO itself — rental POs carry
 * delivery / pickup / fee / tax as their own PO lines, which are classified
 * by description and folded into their PO's main equipment row. Dates,
 * budget, and the in-house rows' charges are user-entered and persist as a
 * JSON blob in Notes under Forecast_Tab = 'Equipment Dashboard', one user-data
 * row per plan item, keyed by unit number or PO-line.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    jobCostDetailAtom, jobUserDataAtom, jobOpenCommitmentsAtom, projectCompleteAtom,
    startDateActualAtom, dataSourceVersionAtom, selectedVersionAtom, selectedJobDataAtom,
    jobPhaseCodesAtom, selectedJobAtom, selectedVersionLockedAtom, userIdAtom, userNameAtom,
} from '../store/company';
import { updateUserDataArray as _updateUserDataArray, fNum, pNum } from '../db/company';
import { useApiTracker } from '../store/useApiStatus';
import moment from 'moment';

const TAB = 'Burn Tool Equipment Phased';
const DASH_TAB = 'Equipment Dashboard';
const RENTAL = '52200';
const FUEL = '52300';
const REPAIRS = '52400';
const TPAY = 'X00-TPAY';
const isInHouse = (c) => c >= '52100' && c <= '52199';

const C = {
    rental: '#3b82f6',
    fuel: '#22c55e',
    repairs: '#f59e0b',
    inhouse: '#8b5cf6',
    ink: '#0f172a',
    sub: '#64748b',
    line: '#e2e8f0',
    panel: '#ffffff',
    soft: '#f8fafc',
    blue: '#1d4ed8',
    red: '#dc2626',
};

const money = (v) => '$' + fNum(pNum(v) || 0, 1);
const num = (v) => fNum(pNum(v) || 0, 1);
const fmtDate = (d) => (d && moment(d).isValid() ? moment(d).format('MMM D, YYYY') : '—');
const normalizeName = (name) => (name + '').replace(/[^a-zA-Z0-9./\s\-^*=]/g, '');

// ── Editable tracking cells (uncontrolled — commit on blur) ──
const inBase = {
    fontSize: 11, padding: '3px 6px', border: '1px solid ' + C.line, borderRadius: 6,
    color: C.ink, backgroundColor: '#fff', fontFamily: 'inherit', boxSizing: 'border-box',
};

const MoneyCell = ({ val, ph, disabled, onCommit, width = 78 }) => {
    const init = (val === undefined || val === null || val === '') ? '' : fNum(pNum(val), 1);
    return (
        <input
            type="text"
            defaultValue={init}
            placeholder={ph || ''}
            disabled={disabled}
            style={{ ...inBase, width, textAlign: 'right', fontVariantNumeric: 'tabular-nums', backgroundColor: disabled ? C.soft : '#fff' }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onBlur={(e) => {
                const raw = e.target.value.trim();
                const next = raw === '' ? '' : pNum(raw);
                const prev = init === '' ? '' : pNum(init);
                if (next !== prev) onCommit(next);
                if (raw !== '') e.target.value = fNum(pNum(raw), 1);
            }}
        />
    );
};

const DateCell = ({ val, disabled, onCommit, width = 118 }) => {
    const init = val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '';
    return (
        <input
            type="date"
            defaultValue={init}
            disabled={disabled}
            style={{ ...inBase, width, backgroundColor: disabled ? C.soft : '#fff' }}
            onBlur={(e) => { if (e.target.value !== init) onCommit(e.target.value); }}
        />
    );
};

// ── KPI card ──
const Kpi = ({ icon, color, label, value, sub }) => (
    <View style={{
        flexGrow: 1, flexBasis: 150, minWidth: 150, backgroundColor: C.panel, borderRadius: 12,
        border: '1px solid ' + C.line, padding: 14, boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: color + '1a', alignItems: 'center', justifyContent: 'center' }}>
                <Icon3 name={icon} size={16} color={color} />
            </View>
            <Text style={{ fontSize: 11, color: C.sub, fontWeight: '600' }}>{label}</Text>
        </View>
        <Text style={{ fontSize: 22, fontWeight: '700', color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
        {sub ? <Text style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{sub}</Text> : null}
    </View>
);

// ── Panel wrapper ──
const Panel = ({ title, hint, right, children, style }) => (
    <View style={{ backgroundColor: C.panel, borderRadius: 12, border: '1px solid ' + C.line, padding: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', ...style }}>
        {title ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <View>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: C.ink }}>{title}</Text>
                    {hint ? <Text style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{hint}</Text> : null}
                </View>
                {right || null}
            </View>
        ) : null}
        {children}
    </View>
);

// ── SVG donut for the cost breakdown ──
const Donut = ({ segments, total }) => {
    const r = 64;
    const sw = 22;
    const cx = 80;
    const cy = 80;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    return (
        <svg width="160" height="160" viewBox="0 0 160 160">
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth={sw} />
            {segments.filter(s => s.value > 0).map((s, i) => {
                const frac = total > 0 ? s.value / total : 0;
                const len = frac * circ;
                const el = (
                    <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={sw}
                        strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset}
                        transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="butt" />
                );
                offset += len;
                return el;
            })}
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="11" fill={C.sub}>Total Cost</text>
            <text x={cx} y={cy + 16} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.ink}>{money(total)}</text>
        </svg>
    );
};

const EquipmentDashboard = () => {
    const trackApi = useApiTracker('equipmentDashboard');
    const updateUserDataArray = (data) => trackApi('updateUserDataArray', 'save', () => _updateUserDataArray(data)).catch(() => {});
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [completionDateForecast] = useAtom(projectCompleteAtom);
    const [startDate] = useAtom(startDateActualAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [userId] = useAtom(userIdAtom);
    const [userName] = useAtom(userNameAtom);
    const [showInvoices, setShowInvoices] = useState(false);

    // ── Rental-tracking fields per plan row (JSON blob in Notes) ──
    const savedTracking = useMemo(() => {
        const map = {};
        (jobUserData || []).forEach(row => {
            if (row?.Forecast_Tab === DASH_TAB && row?.Notes) {
                try { map[row.Forecast_Row_ID] = JSON.parse(row.Notes); } catch (e) { /* malformed blob — ignore */ }
            }
        });
        return map;
    }, [jobUserData]);

    const saveTracking = (rowKey, patch) => {
        const next = { ...(savedTracking[rowKey] || {}) };
        Object.entries(patch).forEach(([k, v]) => {
            if (v === '' || v === null || v === undefined) delete next[k];
            else next[k] = v;
        });
        const json = JSON.stringify(next);
        updateUserDataArray({
            userId: userId || '', userName: userName || '',
            fieldName: 'row', tab: DASH_TAB, rowId: rowKey, version: selectedVersion + '',
            value: '', id: selectedJob, costType: RENTAL, costCode: '', active: 1,
            lockedVersion: lockedVersion, notes: json,
        });
        // Mirror into the atom so the values survive tab switches / remounts.
        setJobUserData(prev => {
            const list = [...(prev || [])];
            const idx = list.findIndex(r => r?.Forecast_Tab === DASH_TAB && r?.Forecast_Row_ID === rowKey);
            if (idx >= 0) list[idx] = { ...list[idx], Notes: json };
            else list.push({ Forecast_Tab: DASH_TAB, Forecast_Row_ID: rowKey, Forecast_Field_Name: 'row', Notes: json, BU_ID: selectedJob, Version_GUID: selectedVersion });
            return list;
        });
    };

    const versionInfo = useMemo(
        () => (versions || []).find(v => v.Version_GUID === selectedVersion) || {},
        [versions, selectedVersion]
    );
    const forecastWeek = useMemo(
        () => moment(versionInfo?.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'),
        [versionInfo]
    );

    const d = useMemo(() => {
        const wk = (date) => moment(date).add(12, 'hours').startOf('isoWeek').format('YYYY-MM-DD');

        // ── Week skeleton: planned start → forecast completion ──
        const startCandidates = [selectedJobData?.Planned_Start_Date, startDate].filter(Boolean).map(x => moment(x));
        const plannedStart = startCandidates.length ? moment.min(startCandidates) : moment(versionInfo?.Version_Start_DateTime);
        const plannedFinish = moment(completionDateForecast || selectedJobData?.Planned_Complete_Date);
        const weeks = [];
        if (plannedStart.isValid() && plannedFinish.isValid() && plannedFinish.isAfter(plannedStart)) {
            let cur = plannedStart.clone().startOf('isoWeek');
            const end = plannedFinish.clone().startOf('isoWeek');
            while (cur.isSameOrBefore(end)) { weeks.push(cur.format('YYYY-MM-DD')); cur.add(1, 'week'); }
        }
        const wkData = {};
        weeks.forEach(w => { wkData[w] = { plannedCount: 0, plannedHours: 0, actualHours: 0, fuel: 0, repairs: 0, rental: 0, activeUnits: new Set() }; });

        // ── Actuals from cost detail ──
        const cat = { rental: 0, fuel: 0, repairs: 0, inhouse: 0 };
        let actualHours = 0;
        const units = {}; // Unit_Number → { code, type, vendor, hours, cost, first, last }
        // Vendor invoices behind the actuals — aggregated per invoice # / date / vendor / bucket
        const invMap = {};
        const addInvoice = (row, cost, bucket) => {
            const inv = row.Supplier_Invoice_Number || '';
            const date = row.GL_Date || row.Consolidated_Date || null;
            const day = date && moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : '';
            const vendor = row.Supplier_Name || '';
            const key = inv + '|' + day + '|' + vendor + '|' + bucket;
            if (!invMap[key]) invMap[key] = { inv, date: day ? moment(day) : null, vendor, bucket, po: row.PO_Number || '', amount: 0 };
            invMap[key].amount += cost;
            if (!invMap[key].po && row.PO_Number) invMap[key].po = row.PO_Number;
        };
        (jobCostDetail || []).forEach(row => {
            const ct = row.Cost_Type_Code;
            const cost = pNum(row.Job_Cost);
            const w = wkData[wk(row.Consolidated_Date)];
            if (ct === RENTAL) { cat.rental += cost; if (w) w.rental += cost; addInvoice(row, cost, 'rental'); return; }
            if (ct === FUEL) { cat.fuel += cost; if (w) w.fuel += cost; addInvoice(row, cost, 'fuel'); return; }
            if (ct === REPAIRS) { cat.repairs += cost; if (w) w.repairs += cost; addInvoice(row, cost, 'repairs'); return; }
            if (isInHouse(ct) && row.Unit_Number !== TPAY) {
                cat.inhouse += cost;
                actualHours += pNum(row.Total_Hours);
                const u = row.Unit_Number;
                if (u) {
                    if (!units[u]) {
                        units[u] = {
                            code: u,
                            type: row.Equipment_Classification_Tier_Name_Description || row.Equipment_Classification_Tier_Name || 'Equipment',
                            vendor: row.Remark_Description || (row.Phase_Code ? (phaseCodes?.[row.Phase_Code] || row.Phase_Code) : 'In-House'),
                            hours: 0, cost: 0, first: null, last: null,
                        };
                    }
                    units[u].hours += pNum(row.Total_Hours);
                    units[u].cost += cost;
                    const rd = moment(row.Consolidated_Date);
                    if (!units[u].first || rd.isBefore(units[u].first)) units[u].first = rd.clone();
                    if (!units[u].last || rd.isAfter(units[u].last)) units[u].last = rd.clone();
                }
                if (w && u) { w.actualHours += pNum(row.Total_Hours); w.activeUnits.add(u); }
            }
        });

        // ── Estimate ledger: planned hours per week + mob/demob from phase dates ──
        // Phased rows live under TAB with 'group=week' IDs; non-phased rows live
        // under 'Burn Tool Equipment' with the bare week as the row ID. The
        // phased ledger wins when both carry entries.
        const phaseStarts = [];
        const phaseEnds = [];
        const phasedPlan = [];
        const regularPlan = [];
        (jobUserData || []).forEach(row => {
            if (row?.Forecast_Tab === TAB) {
                const rid = row?.Forecast_Row_ID || '';
                const sep = rid.includes('=') ? '=' : (rid.includes('*') ? '*' : null);
                if (sep) {
                    const week = rid.split(sep)[1];
                    if (week && moment(week).isValid()) {
                        phasedPlan.push({ week, hrs: pNum(row.Number_Units) * pNum(row.Number_Hours), units: pNum(row.Number_Units) });
                    }
                } else {
                    // group header row — carries phase start/end (mob / demob)
                    if (row.Phase_Start && moment(row.Phase_Start).isValid()) phaseStarts.push(moment(row.Phase_Start));
                    if (row.Phase_End && moment(row.Phase_End).isValid()) phaseEnds.push(moment(row.Phase_End));
                }
            } else if (row?.Forecast_Tab === 'Burn Tool Equipment') {
                const week = (row?.Forecast_Row_ID || '').split('*')[0];
                if (moment(week, 'YYYY-MM-DD', true).isValid()) {
                    regularPlan.push({ week, hrs: pNum(row.Number_Units) * pNum(row.Number_Hours), units: pNum(row.Number_Units) });
                }
            }
        });
        let plannedHoursFuture = 0;
        (phasedPlan.length ? phasedPlan : regularPlan).forEach(({ week, hrs, units }) => {
            const w = wkData[moment(week).startOf('isoWeek').format('YYYY-MM-DD')];
            if (w && hrs) {
                w.plannedHours += hrs;
                w.plannedCount += units;
            }
            if (hrs && moment(week).isAfter(forecastWeek)) plannedHoursFuture += hrs;
        });

        // ── Open commitments (POs) ──
        // Rental POs carry delivery / pickup / fee / tax as their own PO lines
        // ("DELIVERY CHARGE", "PICKUP FEE", "ENVIRONMENTAL FEE", "TX UNIT
        // PROPERTY TAX", ...). Classify those by line description so they fold
        // into their PO's main equipment row instead of listing as items.
        const chargeBucket = (desc) => {
            const s = (desc || '').toLowerCase();
            if (!s || s.includes('truck')) return null; // "pickup truck" / "haul truck" are equipment
            if (/deliver|freight/.test(s)) return 'delivery';
            if (/pick[\s-]?up/.test(s)) return 'pickup';
            if (/\btax\b/.test(s)) return 'tax';
            if (/\bfees?\b/.test(s)) return 'fees';
            return null;
        };
        const poRows = [];
        const chargeLines = {}; // PO_Number → [{ charge, rec }]
        const open = { rental: 0, fuel: 0, repairs: 0, inhouse: 0 };
        (jobOpenCommitments || []).forEach(row => {
            const ct = row.Cost_Type_Code;
            const amt = pNum(row.PO_True_Open_Amount ?? row.PO_Open_Amount);
            let bucket = null;
            if (ct === RENTAL) bucket = 'rental';
            else if (ct === FUEL) bucket = 'fuel';
            else if (ct === REPAIRS) bucket = 'repairs';
            else if (isInHouse(ct)) bucket = 'inhouse';
            if (!bucket) return;
            open[bucket] += amt;
            const rec = {
                bucket,
                po: row.PO_Number || 'Unknown',
                line: row.PO_Line_Number || '',
                vendor: row.Supplier_AB_Name || row.Supplier_Name || 'Unknown Vendor',
                desc: row.PO_Line_Description || row.Account_Description || row.Cost_Type_Description || '',
                orderDate: row.Order_Date,
                expDate: row.Promised_Shipment_Date || row.Rental_Start_Date,
                amount: amt,
                unitPrice: pNum(row.Ordered_Unit_Price),
                ordered: pNum(row.PO_Ordered_Amount),
            };
            const charge = chargeBucket(row.PO_Line_Description);
            if (charge) {
                (chargeLines[rec.po] = chargeLines[rec.po] || []).push({ charge, rec });
                return;
            }
            poRows.push(rec);
        });
        // Fold charge lines into the PO's largest equipment line (full ordered
        // value, not just the open remainder). A PO with only charge lines
        // left keeps them as regular rows so its commitment stays visible.
        const primaryPoLine = {};
        poRows.forEach(p => {
            if (!primaryPoLine[p.po] || Math.abs(p.amount) > Math.abs(primaryPoLine[p.po].amount)) primaryPoLine[p.po] = p;
        });
        Object.entries(chargeLines).forEach(([po, list]) => {
            const host = primaryPoLine[po];
            if (!host) { list.forEach(({ rec }) => poRows.push(rec)); return; }
            host.charges = host.charges || { delivery: 0, pickup: 0, fees: 0, tax: 0 };
            list.forEach(({ charge, rec }) => { host.charges[charge] += rec.ordered || rec.amount; });
        });

        // ── Totals ──
        const costRental = cat.rental + open.rental;
        const costFuel = cat.fuel + open.fuel;
        const costRepairs = cat.repairs + open.repairs;
        const totalEquipmentCost = costRental + costFuel + costRepairs;
        const plannedHours = actualHours + plannedHoursFuture;

        // ── Mob / demob ──
        const mobCandidates = [...phaseStarts];
        const demobCandidates = [...phaseEnds];
        Object.values(units).forEach(u => { if (u.first) mobCandidates.push(u.first); if (u.last) demobCandidates.push(u.last); });
        (poRows || []).forEach(p => { if (p.orderDate && moment(p.orderDate).isValid()) mobCandidates.push(moment(p.orderDate)); });
        if (plannedFinish.isValid()) demobCandidates.push(plannedFinish);
        const earliestMob = mobCandidates.length ? moment.min(mobCandidates) : null;
        const latestDemob = demobCandidates.length ? moment.max(demobCandidates) : null;

        // ── Equipment plan rows (in-house units + PO items) ──
        const planRows = [];
        Object.values(units).sort((a, b) => b.hours - a.hours).forEach(u => {
            planRows.push({
                type: u.type, id: u.code, source: 'In-House', vendor: u.vendor,
                mob: u.first, demob: u.last, po: '', poStatus: 'NA', poDate: null, amount: u.cost, unitPrice: null, hours: u.hours,
            });
        });
        poRows.sort((a, b) => b.amount - a.amount).forEach(p => {
            const label = p.bucket === 'rental' ? 'Rental' : p.bucket === 'fuel' ? 'Fuel' : p.bucket === 'repairs' ? 'Repairs / Maint.' : 'Equipment';
            planRows.push({
                type: p.desc ? normalizeName(p.desc) : label, id: p.line ? p.po + '-' + p.line : p.po,
                source: 'Rented', vendor: p.vendor,
                mob: p.orderDate ? moment(p.orderDate) : null, demob: p.expDate ? moment(p.expDate) : null,
                po: p.po, poStatus: p.amount < 0 ? 'Issues' : p.amount > 0 ? 'Open' : 'Closed',
                poDate: p.orderDate ? moment(p.orderDate) : null, amount: p.amount, unitPrice: p.unitPrice, hours: 0,
                charges: p.charges || null,
            });
        });

        // ── Hours summary ──
        const plannedHoursVisible = weeks.reduce((s, w) => s + wkData[w].plannedHours, 0);
        const variance = actualHours - plannedHoursVisible;

        // ── Invoice rows (rental / fuel / repairs actuals) ──
        const invoiceRows = Object.values(invMap).sort((a, b) => {
            const ad = a.date ? a.date.valueOf() : 0;
            const bd = b.date ? b.date.valueOf() : 0;
            return bd - ad || Math.abs(b.amount) - Math.abs(a.amount);
        });
        const invoiceTotal = invoiceRows.reduce((s, r) => s + r.amount, 0);

        return {
            weeks, wkData, costRental, costFuel, costRepairs, totalEquipmentCost,
            plannedHours, actualHours, plannedHoursVisible, variance,
            inHouseCount: Object.keys(units).length, planRows, earliestMob, latestDemob,
            invoiceRows, invoiceTotal,
        };
    }, [jobCostDetail, jobUserData, jobOpenCommitments, completionDateForecast, startDate, versionInfo, selectedJobData, forecastWeek, selectedVersion, phaseCodes]);

    const segments = [
        { label: 'Rentals', value: d.costRental, color: C.rental },
        { label: 'Fuel', value: d.costFuel, color: C.fuel },
        { label: 'Repairs / Maintenance', value: d.costRepairs, color: C.repairs },
    ];

    const thCell = { fontSize: 9, color: C.sub, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4, padding: '6px 8px', whiteSpace: 'nowrap' };
    const tdCell = { fontSize: 11, color: C.ink, padding: '6px 8px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

    // mini summary line
    const SummaryLine = ({ dot, label, value, bold }) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 5, paddingBottom: 5, borderTop: bold ? '1px solid ' + C.line : 'none' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                {dot ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dot }} /> : null}
                <Text style={{ fontSize: 12, color: bold ? C.ink : C.sub, fontWeight: bold ? '700' : '500' }}>{label}</Text>
            </View>
            <Text style={{ fontSize: 12, color: C.ink, fontWeight: bold ? '700' : '600', fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
        </View>
    );

    return (
        <View style={{ gap: 16 }}>
            {/* Header */}
            <View>
                <Text style={{ fontSize: 20, fontWeight: '700', color: C.ink }}>Equipment Dashboard</Text>
                <Text style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>Plan, track, and forecast all equipment, fuel, and repair activity — in-house and rented.</Text>
            </View>

            {/* KPI cards */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                <Kpi icon="MdSchedule" color={C.inhouse} label="Total Planned Equipment Hours" value={num(d.plannedHours)} sub="Actual + remaining estimate" />
                <Kpi icon="MdSpeed" color={C.rental} label="Total Actual Equipment Hours" value={num(d.actualHours)} sub="In-house, to date" />
                <Kpi icon="MdAttachMoney" color="#16a34a" label="Total Equipment Cost" value={money(d.totalEquipmentCost)} sub="Rental + fuel + repairs" />
                <Kpi icon="MdConstruction" color={C.repairs} label="In-House Equipment Count" value={num(d.inHouseCount)} sub="Distinct units with cost" />
                <Kpi icon="MdEvent" color="#0891b2" label="Earliest Mobilization" value={d.earliestMob ? d.earliestMob.format('MMM D, YYYY') : '—'} sub="First on site" />
                <Kpi icon="MdFlag" color={C.red} label="Latest Demobilization" value={d.latestDemob ? d.latestDemob.format('MMM D, YYYY') : '—'} sub="Forecast" />
            </View>

            {/* Weekly planning + alerts */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
                <Panel title="Weekly Equipment Planning & Forecast" hint={'Forecast start: ' + (forecastWeek.isValid() ? forecastWeek.format('MMM D, YYYY') : '—')} style={{ flexGrow: 1, flexBasis: 620, minWidth: 320 }}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
                        {/* Cost + hours summary */}
                        <View style={{ flexGrow: 0, flexBasis: 240, minWidth: 220 }}>
                            <View style={{ backgroundColor: C.soft, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                                <Text style={{ fontSize: 11, color: C.sub, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Weekly Cost Summary</Text>
                                <SummaryLine dot={C.rental} label="Rental Cost" value={money(d.costRental)} />
                                <SummaryLine dot={C.fuel} label="Fuel Cost" value={money(d.costFuel)} />
                                <SummaryLine dot={C.repairs} label="Repairs / Maintenance" value={money(d.costRepairs)} />
                                <SummaryLine label="Total Equipment Cost" value={money(d.totalEquipmentCost)} bold />
                            </View>
                            <View style={{ backgroundColor: C.soft, borderRadius: 10, padding: 12 }}>
                                <Text style={{ fontSize: 11, color: C.sub, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Hours Summary</Text>
                                <SummaryLine label="Planned Hours" value={num(d.plannedHoursVisible)} />
                                <SummaryLine label="Actual Hours" value={num(d.actualHours)} />
                                <SummaryLine label="Variance" value={(d.variance > 0 ? '+' : '') + num(d.variance)} bold />
                            </View>
                        </View>
                        {/* Weekly grid (scrollable) */}
                        <View style={{ flexGrow: 1, flexBasis: 320, minWidth: 280, overflowX: 'auto' }}>
                            {d.weeks.length ? (
                                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thCell, textAlign: 'left', position: 'sticky', left: 0, backgroundColor: C.panel, zIndex: 1 }}>Week</th>
                                            {d.weeks.map(w => {
                                                const future = moment(w).isAfter(forecastWeek);
                                                return <th key={w} style={{ ...thCell, textAlign: 'right', color: future ? C.blue : C.sub }}>{moment(w).format('M/D')}</th>;
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { key: 'plannedCount', label: 'Planned Count', fmt: (v) => v ? num(v) : '' },
                                            { key: 'activeCount', label: 'Active Count', fmt: (v) => v ? num(v) : '' },
                                            { key: 'plannedHours', label: 'Planned Hours', fmt: (v) => v ? num(v) : '' },
                                            { key: 'actualHours', label: 'Actual Hours', fmt: (v) => v ? num(v) : '' },
                                            { key: 'fuel', label: 'Fuel Cost', fmt: (v) => v ? money(v) : '' },
                                            { key: 'repairs', label: 'Repairs Cost', fmt: (v) => v ? money(v) : '' },
                                        ].map((r, ri) => (
                                            <tr key={r.key} style={{ backgroundColor: ri % 2 ? C.soft : 'transparent' }}>
                                                <td style={{ ...tdCell, textAlign: 'left', fontWeight: '600', color: C.sub, position: 'sticky', left: 0, backgroundColor: ri % 2 ? C.soft : C.panel, zIndex: 1 }}>{r.label}</td>
                                                {d.weeks.map(w => {
                                                    const cell = d.wkData[w];
                                                    const val = r.key === 'activeCount' ? cell.activeUnits.size : cell[r.key];
                                                    const future = moment(w).isAfter(forecastWeek);
                                                    return <td key={w} style={{ ...tdCell, textAlign: 'right', color: future ? C.blue : C.ink }}>{r.fmt(val)}</td>;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <Text style={{ fontSize: 12, color: C.sub, padding: 20 }}>No project weeks — set a start date and completion forecast for this job.</Text>
                            )}
                        </View>
                    </View>
                </Panel>

                {/* Cost breakdown */}
                <Panel title="Equipment Cost Breakdown" style={{ flexGrow: 1, flexBasis: 280, minWidth: 240, alignItems: 'center' }}>
                    <Donut segments={segments} total={d.totalEquipmentCost} />
                    <View style={{ width: '100%', marginTop: 12, gap: 6 }}>
                        {segments.map((s, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: s.color }} />
                                    <Text style={{ fontSize: 12, color: C.ink }}>{s.label}</Text>
                                </View>
                                <Text style={{ fontSize: 12, color: C.sub, fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
                                    {money(s.value)} ({d.totalEquipmentCost > 0 ? Math.round(s.value / d.totalEquipmentCost * 100) : 0}%)
                                </Text>
                            </View>
                        ))}
                    </View>
                </Panel>
            </View>

            {/* Equipment plan */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
                <Panel title="Equipment Plan (Units / Items)" hint={d.planRows.length + ' item' + (d.planRows.length === 1 ? '' : 's') + ' — delivery, pick up, fees, and tax pull from the PO on rented items; monthly rent, off-rent dates, and budget are editable and save automatically'} style={{ flexGrow: 1, flexBasis: 620, minWidth: 320 }}>
                    <View style={{ overflowX: 'auto' }}>
                        {d.planRows.length ? (
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid ' + C.line }}>
                                        {[
                                            ['Equipment Type'], ['Source'], ['PO / Unit #'], ['PO Status'],
                                            ['On Rent'], ['Est. Off Rent'], ['Actual Off Rent'],
                                            ['Monthly Rent', 1],
                                            ['Actual Months', 1], ['Unit Price', 1], ['Total Hours', 1],
                                            ['Equipment Cost', 1], ['Delivery', 1], ['Pick up', 1], ['Fees', 1], ['Tax', 1],
                                            ['Estimated Cost', 1], ['Budget', 1],
                                        ].map(([h, right]) => (
                                            <th key={h} style={{ ...thCell, textAlign: right ? 'right' : 'left' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.planRows.slice(0, 40).map((r, i) => {
                                        // Stable per-item key — namespaced by source, quotes stripped (raw SQL interpolation server-side)
                                        const key = ((r.source === 'In-House' ? 'IH*' : 'PO*') + (r.id || i)).replace(/'/g, '');
                                        const t = savedTracking[key] || {};
                                        const onRent = t.onRent ? moment(t.onRent) : (r.mob || null);
                                        const actOff = t.actOff && moment(t.actOff).isValid() ? moment(t.actOff) : null;
                                        const actualMonths = onRent && onRent.isValid()
                                            ? Math.max(0, (actOff || moment()).diff(onRent, 'days') / 30.44) : null;
                                        const equipCost = t.equipCost !== undefined ? pNum(t.equipCost) : pNum(r.amount);
                                        // PO-backed rows pull delivery / pickup / fees / tax straight
                                        // off the PO's charge lines; only in-house rows hand-enter them.
                                        const fromPo = r.source !== 'In-House';
                                        const charges = fromPo
                                            ? { delivery: pNum(r.charges?.delivery), pickup: pNum(r.charges?.pickup), fees: pNum(r.charges?.fees), tax: pNum(r.charges?.tax) }
                                            : { delivery: pNum(t.delivery), pickup: pNum(t.pickup), fees: pNum(t.fees), tax: pNum(t.tax) };
                                        const estCost = equipCost + charges.delivery + charges.pickup + charges.fees + charges.tax;
                                        const budget = t.budget !== undefined ? pNum(t.budget) : null;
                                        const overBudget = budget !== null && estCost > budget;
                                        const locked = !!lockedVersion;
                                        const commit = (field) => (v) => saveTracking(key, { [field]: v });
                                        // Remount the row when its saved blob changes so uncontrolled inputs resync
                                        const domKey = selectedJob + '|' + selectedVersion + '|' + key + '|' + JSON.stringify(t);
                                        return (
                                            <tr key={domKey} style={{ borderBottom: '1px solid ' + C.line }}>
                                                <td style={{ ...tdCell, whiteSpace: 'normal', maxWidth: 220 }}>{r.type}</td>
                                                <td style={{ ...tdCell, whiteSpace: 'normal', maxWidth: 200 }}>
                                                    {r.source === 'In-House' ? (
                                                        <Text style={{ fontSize: 10, fontWeight: '700', color: C.inhouse, backgroundColor: C.inhouse + '14', padding: '2px 7px', borderRadius: 999 }}>In-House</Text>
                                                    ) : (r.vendor || '—')}
                                                </td>
                                                <td style={tdCell}>{r.id || '—'}</td>
                                                <td style={tdCell}>{r.poStatus || '—'}</td>
                                                <td style={tdCell}><DateCell val={t.onRent || r.mob} disabled={locked} onCommit={commit('onRent')} /></td>
                                                <td style={tdCell}><DateCell val={t.estOff || r.demob} disabled={locked} onCommit={commit('estOff')} /></td>
                                                <td style={tdCell}><DateCell val={t.actOff} disabled={locked} onCommit={commit('actOff')} /></td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}><MoneyCell val={t.monthlyRent} ph={r.unitPrice ? money(r.unitPrice) : ''} disabled={locked} onCommit={commit('monthlyRent')} width={80} /></td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}>{actualMonths === null ? '—' : fNum(actualMonths, 2)}</td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}>{r.unitPrice ? money(r.unitPrice) : '—'}</td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}>{r.hours ? num(r.hours) : '—'}</td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}><MoneyCell val={t.equipCost} ph={r.amount ? money(r.amount) : ''} disabled={locked} onCommit={commit('equipCost')} width={90} /></td>
                                                {['delivery', 'pickup', 'fees', 'tax'].map(f => (
                                                    <td key={f} style={{ ...tdCell, textAlign: 'right' }}>
                                                        {fromPo
                                                            ? (charges[f] ? money(charges[f]) : '—')
                                                            : <MoneyCell val={t[f]} disabled={locked} onCommit={commit(f)} width={70} />}
                                                    </td>
                                                ))}
                                                <td style={{ ...tdCell, textAlign: 'right', fontWeight: '600', color: overBudget ? C.red : C.ink }}>{money(estCost)}</td>
                                                <td style={{ ...tdCell, textAlign: 'right' }}><MoneyCell val={t.budget} disabled={locked} onCommit={commit('budget')} width={90} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <Text style={{ fontSize: 12, color: C.sub, padding: 20 }}>No equipment units, rentals, or open POs yet for this job/version.</Text>
                        )}
                        {d.planRows.length > 40 ? <Text style={{ fontSize: 11, color: C.sub, paddingTop: 8 }}>Showing 40 of {d.planRows.length} items.</Text> : null}
                    </View>
                </Panel>
            </View>

            {/* Equipment invoices (expandable) */}
            <Panel style={{ padding: 0 }}>
                <Pressable onPress={() => setShowInvoices(!showInvoices)} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, cursor: 'pointer' }}>
                    <View>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: C.ink }}>Equipment Invoices</Text>
                        <Text style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>
                            {d.invoiceRows.length + ' invoice' + (d.invoiceRows.length === 1 ? '' : 's') + ' · ' + money(d.invoiceTotal) + ' — rental, fuel, and repairs actuals'}
                        </Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={{ fontSize: 11, color: C.sub, fontWeight: '600' }}>{showInvoices ? 'Hide' : 'Show'}</Text>
                        <Icon3 name={showInvoices ? 'MdExpandLess' : 'MdExpandMore'} size={20} color={C.sub} />
                    </View>
                </Pressable>
                {showInvoices ? (
                    <View style={{ padding: 16, paddingTop: 0, overflowX: 'auto' }}>
                        {d.invoiceRows.length ? (
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid ' + C.line }}>
                                        {['Invoice #', 'Vendor', 'Category', 'PO #', 'Date', 'Amount'].map((h, i) => (
                                            <th key={h} style={{ ...thCell, textAlign: i === 5 ? 'right' : 'left' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.invoiceRows.slice(0, 100).map((r, i) => {
                                        const catLabel = r.bucket === 'rental' ? 'Rental' : r.bucket === 'fuel' ? 'Fuel' : 'Repairs / Maint.';
                                        const catColor = r.bucket === 'rental' ? C.rental : r.bucket === 'fuel' ? C.fuel : C.repairs;
                                        return (
                                            <tr key={i} style={{ borderBottom: '1px solid ' + C.line }}>
                                                <td style={{ ...tdCell, fontWeight: '600' }}>{r.inv || '—'}</td>
                                                <td style={{ ...tdCell, whiteSpace: 'normal', maxWidth: 220 }}>{r.vendor ? normalizeName(r.vendor) : '—'}</td>
                                                <td style={tdCell}>
                                                    <Text style={{ fontSize: 10, fontWeight: '700', color: catColor, backgroundColor: catColor + '14', padding: '2px 7px', borderRadius: 999 }}>{catLabel}</Text>
                                                </td>
                                                <td style={tdCell}>{r.po || '—'}</td>
                                                <td style={tdCell}>{r.date ? fmtDate(r.date) : '—'}</td>
                                                <td style={{ ...tdCell, textAlign: 'right', color: r.amount < 0 ? C.red : C.ink }}>{money(r.amount)}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td colSpan={5} style={{ ...tdCell, fontWeight: '700', color: C.sub }}>Total</td>
                                        <td style={{ ...tdCell, textAlign: 'right', fontWeight: '700' }}>{money(d.invoiceTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <Text style={{ fontSize: 12, color: C.sub, padding: 20 }}>No equipment invoices posted yet for this job.</Text>
                        )}
                        {d.invoiceRows.length > 100 ? <Text style={{ fontSize: 11, color: C.sub, paddingTop: 8 }}>Showing 100 of {d.invoiceRows.length} invoices.</Text> : null}
                    </View>
                ) : null}
            </Panel>
        </View>
    );
};

export default EquipmentDashboard;
